import React, { useState, useCallback } from 'react';
import { CLASSIFICATION_OPTIONS } from '../constants';
import { parseLabeledBilingualBlocks } from '../utils/reportTranslation';

// ── Engagement type options (mirrors backend templates) ───────────────────────
const ENGAGEMENT_OPTIONS = [
  { value: 'general_consultant', label: 'General Consultant Report' },
  { value: 'vishing_assessment', label: 'Vishing Assessment' },
  { value: 'phishing_program_review', label: 'Phishing Program Review' },
  { value: 'nation_state_ttp_brief', label: 'Nation State TTP Brief' },
  { value: 'defense_operations_brief', label: 'Defense Operations Brief' },
];

// ── Section-categorisation regexes ────────────────────────────────────────────
const RISK_RE      = /risk|vulnerab|weakness|gap|threat|exposure|attack|compromise/i;
const REC_RE       = /recommend|action item|implement|mitigat|control|remedi|should\b|must\b|improve/i;
const EXEC_RE      = /exec|summary|overview|background|scope|purpose|introduction|about/i;
const NEXT_RE      = /next step|follow.?up|timeline|deliverable|milestone/i;
const EX_RISK_RE   = /exercise.*execution.*risk|execution.*risk.*summary|operational.*risk/i;

// ── Parsing helpers ───────────────────────────────────────────────────────────
function extractPoints(lines) {
  return lines
    .map((l) => l.trim().replace(/^[-*•·▸]\s+/, '').replace(/^\d+[.)]\s+/, ''))
    .filter((l) => l.length > 3);
}

function categorize(heading, text) {
  const s = `${heading} ${text}`.toLowerCase();
  if (EXEC_RE.test(s)) return 'executive';
  if (EX_RISK_RE.test(heading)) return 'exercise-risk';
  if (NEXT_RE.test(s)) return 'next-steps';
  if (REC_RE.test(s)) return 'recommendations';
  if (RISK_RE.test(s)) return 'risk';
  return 'findings';
}

// ── Pretext parsing ───────────────────────────────────────────────────────────
const PRETEXT_PARENT_RE = /^(?:#{1,3}\s*)?pretext\s+(\d+)(?:\s*[-–]\s*.+)?$/i;

// Maps inline "Field label: value" patterns → pretext object keys
const PRETEXT_INLINE_FIELDS = [
  [/persona\s+name/i,                      'personaName'],
  [/persona\s+role/i,                      'personaRole'],
  [/org(?:anizational)?\s+affiliation/i,   'orgAffiliation'],
  [/why.+(?:credible|target|finds)/i,      'credibilityReason'],
  [/(?:supporting\s+)?legend|backstory/i,  'legend'],
  [/communication\s+channel/i,             'channel'],
  [/primary\s+objective/i,                 'primaryObjective'],
  [/success\s+def(?:inition)?/i,           'successDefinition'],
  [/immediate\s+cred/i,                    'immediateCredibility'],
];

function emptyPretext() {
  return {
    personaName: '', personaRole: '', orgAffiliation: '', credibilityReason: '',
    legend: '', channel: '', primaryObjective: '', successDefinition: '',
    openingHook: '', immediateCredibility: '', conversationFlow: '',
    objectiveDelivery: '', fullScript: '', additionalNotes: '',
  };
}

function parsePretextList(raw) {
  if (!/pretext/i.test(raw)) return [];

  const lines = raw.split('\n');
  const pretexts = [];
  let pt = null;
  let activeSection = null; // key of a multi-line section currently being accumulated
  let sectionLines = [];

  const flushSection = () => {
    if (pt && activeSection && sectionLines.length) {
      const existing = pt[activeSection];
      pt[activeSection] = existing
        ? `${existing}\n${sectionLines.join('\n')}`.trim()
        : sectionLines.join('\n').trim();
    }
    sectionLines = [];
  };

  const tryInlineField = (line) => {
    const clean = line.replace(/^[-*•·▸]\s*/, '');
    for (const [re, key] of PRETEXT_INLINE_FIELDS) {
      const m = clean.match(new RegExp(`^(?:${re.source})\\s*[:–-]\\s*(.+)`, 'i'));
      if (m && pt) { pt[key] = m[1].trim(); return true; }
    }
    return false;
  };

  for (const line of lines) {
    const t = line.trim();
    if (!t) continue;

    // New parent pretext block
    if (PRETEXT_PARENT_RE.test(t)) {
      flushSection();
      if (pt) pretexts.push(pt);
      pt = emptyPretext();
      activeSection = null;
      continue;
    }

    if (!pt) continue;

    // Strip markdown heading markers and leading numbers to normalise sub-section headings
    const stripped = t.replace(/^#{1,3}\s+/, '').replace(/^\d+[.)]\s+/, '');

    if      (/pretext\s+identity/i.test(stripped))     { flushSection(); activeSection = null;               continue; }
    else if (/primary\s+obj/i.test(stripped))           { flushSection(); activeSection = null;               continue; }
    else if (/opening\s+hook/i.test(stripped))          { flushSection(); activeSection = 'openingHook';      continue; }
    else if (/conversation\s+flow/i.test(stripped))     { flushSection(); activeSection = 'conversationFlow'; continue; }
    else if (/objective\s+delivery/i.test(stripped))    { flushSection(); activeSection = 'objectiveDelivery'; continue; }
    else if (/full\s+script/i.test(stripped))           { flushSection(); activeSection = 'fullScript';       continue; }
    else if (/additional\s+notes/i.test(stripped))      { flushSection(); activeSection = 'additionalNotes';  continue; }

    // Try extracting an inline "Label: value" field; otherwise accumulate to active section
    if (!tryInlineField(t) && activeSection) {
      sectionLines.push(t.replace(/^[-*•·▸]\s+/, ''));
    }
  }

  flushSection();
  if (pt) pretexts.push(pt);

  return pretexts.filter((p) => Object.values(p).some((v) => v));
}

function parseInput(raw, engType, secondaryLang) {
  const lines = raw.split('\n');
  const chunks = [];
  let cur = { heading: 'Overview', lines: [] };

  for (const line of lines) {
    const t = line.trim();
    const md   = t.match(/^#{1,3}\s+(.+)/);
    const bold = t.match(/^\*\*(.+?)\*\*:?$/);
    const caps = t.match(/^([A-Z][A-Z\s&/:()-]{4,})$/);
    if (md || bold || caps) {
      if (cur.lines.length) chunks.push(cur);
      cur = { heading: (md?.[1] || bold?.[1] || caps?.[1] || '').trim(), lines: [] };
    } else if (t) {
      cur.lines.push(t);
    }
  }
  if (cur.lines.length) chunks.push(cur);

  const sections = chunks
    .map((c) => ({
      heading: c.heading,
      points: extractPoints(c.lines),
      rawLines: c.lines,
      category: categorize(c.heading, c.lines.join(' ')),
    }))
    .filter((s) => s.points.length > 0 || s.rawLines.length > 0);

  const label = ENGAGEMENT_OPTIONS.find((e) => e.value === engType)?.label ?? 'Assessment';
  const execPts    = sections.filter((s) => s.category === 'executive').flatMap((s) => s.points);
  const findPts    = sections.filter((s) => s.category === 'findings').flatMap((s) => s.points);
  const riskPts    = sections.filter((s) => s.category === 'risk').flatMap((s) => s.points);
  const recPts     = sections.filter((s) => s.category === 'recommendations').flatMap((s) => s.points);
  const nextPts    = sections.filter((s) => s.category === 'next-steps').flatMap((s) => s.points);
  const exRiskSecs = sections.filter((s) => s.category === 'exercise-risk');
  const exerciseRiskSummary = exRiskSecs.length
    ? exRiskSecs.map((s) => s.rawLines.join('\n')).join('\n\n').trim()
    : '';

  return {
    title: label,
    date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    engagementType: label,
    executiveSummary: execPts.length ? execPts : findPts.slice(0, 3),
    keyFindings: findPts.slice(0, 8),
    sections: sections.filter((s) => s.category === 'findings' || s.category === 'risk'),
    riskFactors: riskPts,
    recommendations: recPts,
    nextSteps: nextPts,
    exerciseRiskSummary,
    pretextList: parsePretextList(raw),
    secondaryLang: secondaryLang || '',
  };
}

function buildSlides(data) {
  const slides = [
    { type: 'title', title: data.title, subtitle: '' },
  ];
  if (data.executiveSummary.length > 0)
    slides.push({ type: 'summary', title: 'Executive Summary', bullets: data.executiveSummary.slice(0, 5) });
  for (let i = 0; i < Math.min(data.keyFindings.length, 8); i += 4)
    slides.push({
      type: 'findings',
      title: i === 0 ? 'Key Findings' : 'Key Findings (cont.)',
      bullets: data.keyFindings.slice(i, i + 4),
    });
  if (data.pretextList?.length) {
    data.pretextList.forEach((pt, idx) => {
      const personaLabel = pt.personaName || pt.primaryObjective || '';
      const title = `Pretext ${idx + 1}${personaLabel ? ` – ${personaLabel}` : ''}`;
      const bullets = [
        pt.personaName   && `Persona: ${pt.personaName}${pt.personaRole ? ` (${pt.personaRole})` : ''}`,
        pt.orgAffiliation && `Affiliation: ${pt.orgAffiliation}`,
        pt.channel        && `Channel: ${pt.channel}`,
        pt.primaryObjective && `Objective: ${pt.primaryObjective}`,
        pt.successDefinition && `Success: ${pt.successDefinition}`,
        pt.openingHook    && `Hook: ${pt.openingHook}`,
      ].filter(Boolean);
      slides.push({ type: 'pretext', title, bullets, pretext: pt });
    });
  }
  if (data.riskFactors.length > 0)
    slides.push({ type: 'risk', title: 'Risk Assessment', bullets: data.riskFactors.slice(0, 5) });
  if (data.exerciseRiskSummary)
    slides.push({
      type: 'exercise-risk',
      title: 'Operational Execution Risk Summary',
      bullets: data.exerciseRiskSummary.split('\n').filter((l) => l.trim()).slice(0, 6),
    });
  if (data.recommendations.length > 0)
    slides.push({ type: 'recommendations', title: 'Recommendations', bullets: data.recommendations.slice(0, 5) });
  if (data.nextSteps.length > 0)
    slides.push({ type: 'next-steps', title: 'Next Steps & Action Items', bullets: data.nextSteps.slice(0, 5) });
  return slides;
}

function buildMarkdown(data, meta) {
  const lines = [];
  const title  = meta.reportTitle || data.title;
  const date   = meta.reportDate  || data.date;
  const forStr = meta.preparedFor || meta.clientName || 'Client';

  lines.push(`# ${title}${meta.clientName ? ` — ${meta.clientName}` : ''}\n`);
  lines.push(`**Prepared for:** ${forStr}  `);
  lines.push(`**Date:** ${date}  `);
  lines.push(`**Classification:** ${meta.classification}\n`);
  lines.push(`---\n`);

  // 1. Executive Summary
  if (data.executiveSummary.length) {
    lines.push(`## Executive Summary\n`);
    data.executiveSummary.forEach((p) => lines.push(`${p}\n`));
    lines.push('');
  }

  // Non-pretext sections (findings, risk factors, etc.)
  data.sections.forEach((s) => {
    lines.push(`## ${s.heading}\n`);
    s.points.forEach((p) => lines.push(`- ${p}`));
    lines.push('');
  });
  if (data.riskFactors.length) {
    lines.push(`## Risk Factors\n`);
    data.riskFactors.forEach((r) => lines.push(`- ${r}`));
    lines.push('');
  }
  if (data.keyFindings.length) {
    lines.push(`## Key Findings\n`);
    data.keyFindings.forEach((f, i) => lines.push(`${i + 1}. ${f}`));
    lines.push('');
  }

  // 2. Developed Pretexts (simple list: name + channel)
  if (data.pretextList?.length) {
    lines.push(`## Developed Pretexts\n`);
    data.pretextList.forEach((pt, idx) => {
      const label = pt.personaName || pt.primaryObjective || `Pretext ${idx + 1}`;
      const channelStr = pt.channel ? ` — Channel: ${pt.channel}` : '';
      lines.push(`- ${idx + 1}. ${label}${channelStr}`);
    });
    lines.push('');
  }

  // 3. Detailed Pretext (each expanded with full field structure)
  if (data.pretextList?.length) {
    lines.push(`## Detailed Pretexts\n`);
    data.pretextList.forEach((pt, idx) => {
      const label = pt.personaName || pt.primaryObjective || `Pretext ${idx + 1}`;
      lines.push(`### Pretext ${idx + 1} — ${label}\n`);

      if (pt.channel)           lines.push(`Channel: ${pt.channel}`);
      lines.push(`Target: `);
      lines.push(`Primary Objective: ${pt.primaryObjective || ''}\n`);

      lines.push(`#### 1. Pretext Identity`);
      lines.push(`Persona name: ${pt.personaName || ''}`);
      lines.push(`Persona role: ${pt.personaRole || ''}`);
      lines.push(`Organizational affiliation: ${pt.orgAffiliation || ''}`);
      lines.push(`Why the target finds this persona credible: ${pt.credibilityReason || ''}`);
      if (pt.legend) lines.push(`Supporting legend / backstory: ${pt.legend}`);
      lines.push(`Communication channel: ${pt.channel || ''}\n`);

      lines.push(`#### 2. Primary Objective`);
      lines.push(`Primary objective: ${pt.primaryObjective || ''}`);
      lines.push(`Success definition: ${pt.successDefinition || ''}\n`);

      lines.push(`#### 4. Opening Hook`);
      lines.push(`Opening hook (first 30 seconds / message): ${pt.openingHook || ''}`);
      lines.push(`Immediate credibility before disengagement: ${pt.immediateCredibility || ''}\n`);

      lines.push(`#### 5. Conversation Flow\n`);
      if (pt.conversationFlow) lines.push(pt.conversationFlow);
      lines.push('');

      lines.push(`#### 6. Objective Delivery Moment\n`);
      if (pt.objectiveDelivery) lines.push(pt.objectiveDelivery);
      lines.push('');

      lines.push(`#### Full Script\n`);
      if (pt.fullScript) lines.push(pt.fullScript);
      lines.push('');

      lines.push(`Additional Notes:`);
      if (pt.additionalNotes) lines.push(pt.additionalNotes);
      lines.push('');

      lines.push('---\n');
    });
  }

  // 4. Exercise Execution Risk Summary
  if (data.exerciseRiskSummary) {
    lines.push(`## Operational Execution Risk Summary\n`);
    lines.push(data.exerciseRiskSummary);
    lines.push('');
  }

  // 5. Recommendations
  if (data.recommendations.length) {
    lines.push(`## Recommendations\n`);
    data.recommendations.forEach((r, i) => lines.push(`${i + 1}. ${r}`));
    lines.push('');
  }
  if (data.nextSteps.length) {
    lines.push(`## Next Steps\n`);
    data.nextSteps.forEach((s) => lines.push(`- ${s}`));
    lines.push('');
  }
  lines.push(`---`);
  return lines.join('\n');
}

// ── Classification badge colours ──────────────────────────────────────────────
function classificationClass(c) {
  if (c === 'SECRET')       return 'bg-red-900/40 text-red-300 border border-red-500/50';
  if (c === 'CONFIDENTIAL') return 'bg-amber-900/40 text-amber-300 border border-amber-500/50';
  if (c === 'OFFICIAL')     return 'bg-blue-900/40 text-blue-300 border border-blue-500/50';
  return 'bg-gray-800 text-gray-300 border border-gray-600';
}

// ── Sub-components ────────────────────────────────────────────────────────────
function SectionHeading({ children }) {
  return <h3 className="text-sm font-semibold text-teal-400 uppercase tracking-wide mb-2">{children}</h3>;
}

function BulletList({ items, numbered = false }) {
  if (!items?.length) return null;
  return (
    <ul className="space-y-1.5">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2 text-gray-200 text-sm">
          <span className="text-teal-400 mt-0.5 shrink-0">{numbered ? `${i + 1}.` : '→'}</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function BilingualField({ value, preWrap = false }) {
  if (!value) return null;
  const blocks = parseLabeledBilingualBlocks(value);
  if (!blocks) {
    return preWrap
      ? <p className="text-sm text-gray-200 whitespace-pre-wrap leading-relaxed">{value}</p>
      : <span className="text-gray-200">{value}</span>;
  }
  return (
    <div className="space-y-2">
      {blocks.map(({ label: langLabel, content }, i) => (
        <div key={i} className={i > 0 ? 'pt-2 border-t border-gray-700/50' : ''}>
          <span className={`inline-block mb-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${
            i === 0 ? 'border-teal-500/40 text-teal-400 bg-teal-950/30' : 'border-cyan-500/40 text-cyan-400 bg-cyan-950/30'
          }`}>
            {langLabel}
          </span>
          {content && (
            preWrap
              ? <p className="text-sm text-gray-200 whitespace-pre-wrap leading-relaxed">{content}</p>
              : <p className="text-sm text-gray-200">{content}</p>
          )}
        </div>
      ))}
    </div>
  );
}

function FieldRow({ label, value }) {
  if (!value) return null;
  const blocks = parseLabeledBilingualBlocks(value);
  if (!blocks) {
    return (
      <div className="flex items-start gap-2 text-sm">
        <dt className="text-gray-500 shrink-0" style={{ minWidth: '11rem' }}>{label}</dt>
        <dd className="text-gray-200">{value}</dd>
      </div>
    );
  }
  return (
    <div className="text-sm space-y-1">
      <dt className="text-gray-500">{label}</dt>
      <dd className="pl-3 border-l-2 border-gray-700">
        <BilingualField value={value} />
      </dd>
    </div>
  );
}

function PretextCard({ pretext, index }) {
  const hasIdentity = pretext.personaName || pretext.personaRole || pretext.orgAffiliation
    || pretext.credibilityReason || pretext.legend || pretext.channel;
  const hasObjective = pretext.primaryObjective || pretext.successDefinition;
  const hasHook = pretext.openingHook || pretext.immediateCredibility;

  return (
    <div className="rounded-lg border border-teal-500/20 bg-gray-900/50 overflow-hidden">
      {/* Card header */}
      <div className="px-4 py-2.5 bg-teal-950/40 border-b border-teal-500/20 flex items-center gap-2">
        <span className="text-xs font-bold text-teal-500 bg-teal-500/10 rounded px-1.5 py-0.5 border border-teal-500/20">
          #{index + 1}
        </span>
        <h4 className="text-sm font-semibold text-teal-300">
          {pretext.personaName || pretext.primaryObjective || `Pretext ${index + 1}`}
        </h4>
        {pretext.channel && (
          <span className="ml-auto text-[10px] text-gray-500 border border-gray-700 rounded px-1.5 py-0.5">
            {pretext.channel}
          </span>
        )}
      </div>

      <div className="p-4 space-y-5">
        {/* 1. Pretext Identity */}
        {hasIdentity && (
          <div>
            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2">
              1. Pretext Identity
            </p>
            <dl className="space-y-1.5">
              <FieldRow label="Persona name"              value={pretext.personaName} />
              <FieldRow label="Persona role"              value={pretext.personaRole} />
              <FieldRow label="Org. affiliation"          value={pretext.orgAffiliation} />
              <FieldRow label="Why credible"              value={pretext.credibilityReason} />
              <FieldRow label="Legend / backstory"        value={pretext.legend} />
              <FieldRow label="Communication channel"     value={pretext.channel} />
            </dl>
          </div>
        )}

        {/* 2. Primary Objective */}
        {hasObjective && (
          <div>
            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2">
              2. Primary Objective
            </p>
            <dl className="space-y-1.5">
              <FieldRow label="Primary objective"    value={pretext.primaryObjective} />
              <FieldRow label="Success definition"   value={pretext.successDefinition} />
            </dl>
          </div>
        )}

        {/* 4. Opening Hook */}
        {hasHook && (
          <div>
            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2">
              4. Opening Hook
            </p>
            {pretext.openingHook && (
              <div className="text-sm text-gray-200 italic bg-gray-800/60 rounded px-3 py-2 mb-2 leading-relaxed">
                &ldquo;{pretext.openingHook}&rdquo;
              </div>
            )}
            {pretext.immediateCredibility && (
              <dl>
                <FieldRow label="Immediate credibility" value={pretext.immediateCredibility} />
              </dl>
            )}
          </div>
        )}

        {/* 5. Conversation Flow */}
        {pretext.conversationFlow && (
          <div>
            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2">
              5. Conversation Flow
            </p>
            <BilingualField value={pretext.conversationFlow} preWrap />
          </div>
        )}

        {/* 6. Objective Delivery Moment */}
        {pretext.objectiveDelivery && (
          <div>
            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2">
              6. Objective Delivery Moment
            </p>
            <BilingualField value={pretext.objectiveDelivery} preWrap />
          </div>
        )}

        {/* Full Script */}
        {pretext.fullScript && (
          <div>
            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Full Script
            </p>
            {parseLabeledBilingualBlocks(pretext.fullScript) ? (
              <BilingualField value={pretext.fullScript} preWrap />
            ) : (
              <pre className="text-xs text-gray-300 whitespace-pre-wrap font-mono bg-gray-800/50 rounded p-3 leading-relaxed">
                {pretext.fullScript}
              </pre>
            )}
          </div>
        )}

        {/* Additional Notes */}
        {pretext.additionalNotes && (
          <div>
            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Additional Notes
            </p>
            <BilingualField value={pretext.additionalNotes} preWrap />
          </div>
        )}
      </div>
    </div>
  );
}

function SlidePreview({ slide, slideIndex, totalSlides, clientName, classification, reportDate }) {
  if (!slide) return null;
  return (
    <div
      className="w-full rounded-lg overflow-hidden border border-teal-500/30"
      style={{ aspectRatio: '16/9', display: 'flex', flexDirection: 'column' }}
    >
      {/* Brand bar */}
      <div className="bg-teal-500 px-4 py-1.5 flex items-center justify-between shrink-0">
        <span className="text-black/70 text-[9px]">{classification}</span>
      </div>

      {/* Slide body */}
      <div className="flex-1 bg-gray-900 px-6 py-4 flex flex-col gap-3 min-h-0 overflow-hidden">
        {slide.type === 'title' ? (
          <div className="flex flex-col justify-center h-full">
            <p className="text-white text-xl font-semibold leading-snug mb-2">{slide.title}</p>
            {slide.subtitle && <p className="text-teal-400 text-sm">{slide.subtitle}</p>}
            {clientName && <p className="text-gray-400 text-xs mt-3">Client: {clientName}</p>}
            {reportDate && <p className="text-gray-400 text-xs mt-1">{reportDate}</p>}
          </div>
        ) : slide.type === 'pretext' && slide.pretext ? (
          <>
            <p className="text-teal-300 font-semibold text-base border-b border-teal-500/20 pb-2 shrink-0">
              {slide.title}
            </p>
            <div className="flex gap-4 flex-1 overflow-hidden">
              {/* Identity column */}
              <div className="flex-1 space-y-1.5 overflow-hidden">
                <p className="text-[8px] font-bold text-teal-500/70 uppercase tracking-widest mb-1">Identity</p>
                {[
                  ['Persona', slide.pretext.personaName],
                  ['Role', slide.pretext.personaRole],
                  ['Affiliation', slide.pretext.orgAffiliation],
                  ['Channel', slide.pretext.channel],
                ].filter(([, v]) => v).map(([k, v]) => (
                  <div key={k} className="flex gap-1.5 text-[9px] leading-snug">
                    <span className="text-gray-500 shrink-0 w-14">{k}</span>
                    <span className="text-gray-200">{v}</span>
                  </div>
                ))}
              </div>
              {/* Objective column */}
              <div className="flex-1 space-y-1.5 overflow-hidden border-l border-teal-500/10 pl-4">
                <p className="text-[8px] font-bold text-teal-500/70 uppercase tracking-widest mb-1">Objective</p>
                {[
                  ['Goal', slide.pretext.primaryObjective],
                  ['Success', slide.pretext.successDefinition],
                  ['Hook', slide.pretext.openingHook],
                ].filter(([, v]) => v).map(([k, v]) => (
                  <div key={k} className="flex gap-1.5 text-[9px] leading-snug">
                    <span className="text-gray-500 shrink-0 w-14">{k}</span>
                    <span className="text-gray-200 line-clamp-2">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : (
          <>
            <p className="text-teal-400 font-semibold text-base border-b border-teal-500/20 pb-2 shrink-0">
              {slide.title}
            </p>
            <ul className="space-y-2 overflow-hidden flex-1">
              {(slide.bullets || []).map((b, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-teal-400 mt-2 shrink-0" />
                  <span className="text-gray-200 text-xs leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      {/* Footer */}
      <div className="bg-gray-950 px-4 py-1 flex items-center justify-between shrink-0">
        <span className="text-gray-600 text-[9px]">asksunshine.com</span>
        <span className="text-gray-600 text-[9px]">{slideIndex + 1} / {totalSlides}</span>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function ReportBuilderPanel({
  initialClientName = '',
  initialClassification = 'CONFIDENTIAL',
  onSendToChat,
}) {
  const [activeView, setActiveView]           = useState('input');
  const [rawInput, setRawInput]               = useState('');
  const [clientName, setClientName]           = useState(initialClientName);
  const [preparedFor, setPreparedFor]         = useState('');
  const [engagementType, setEngagementType]   = useState('general_consultant');
  const [classification, setClassification]   = useState(initialClassification);
  const [reportDate, setReportDate]           = useState('');
  const [reportTitle, setReportTitle]         = useState('');
  const [secondaryLang, setSecondaryLang]     = useState('');
  const [reportData, setReportData]           = useState(null);
  const [slides, setSlides]                   = useState([]);
  const [mdReport, setMdReport]               = useState('');
  const [activeSlide, setActiveSlide]         = useState(0);
  const [copyFeedback, setCopyFeedback]       = useState('');

  const hasOutput = reportData !== null && slides.length > 0;

  const copyText = useCallback((text, label) => {
    navigator.clipboard.writeText(text).catch(() => {
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    });
    setCopyFeedback(label);
    setTimeout(() => setCopyFeedback(''), 2000);
  }, []);

  const handleGenerate = useCallback(() => {
    if (!rawInput.trim()) return;
    const data = parseInput(rawInput, engagementType, secondaryLang);
    if (reportTitle) data.title = reportTitle;
    if (reportDate)  data.date  = reportDate;
    const newSlides = buildSlides(data);
    const md = buildMarkdown(data, { clientName, preparedFor, classification, reportDate, reportTitle });
    setReportData(data);
    setSlides(newSlides);
    setMdReport(md);
    setActiveSlide(0);
    setActiveView('report');
  }, [rawInput, engagementType, secondaryLang, reportTitle, reportDate, clientName, preparedFor, classification]);

  const handleSendToChat = useCallback(() => {
    if (!mdReport || !onSendToChat) return;
    const bilingualNote = secondaryLang
      ? `\n\nBILINGUAL REQUIREMENT: Every section must appear in both the analyst language (English) and the secondary language (${secondaryLang}). Use labeled blocks:\n[English]\n<text>\n\n[${secondaryLang}]\n<text>`
      : '';
    onSendToChat(
      `Please review and enhance this structured report draft following the mandatory report structure:\n` +
      `1. Executive Summary\n` +
      `2. Developed Pretexts (simple list: pretext name + channel only)\n` +
      `3. Detailed Pretext (for each pretext, full field layout: Channel, Target, Primary Objective, ` +
      `then sections: 1. Pretext Identity, 2. Primary Objective, 4. Opening Hook, 5. Conversation Flow, ` +
      `6. Objective Delivery Moment, Full Script, Additional Notes)\n` +
      `4. Operational Execution Risk Summary\n` +
      `5. Recommendations${bilingualNote}\n\n` +
      `Add depth, professional language, and complete any gaps:\n\n${mdReport}`,
    );
  }, [mdReport, secondaryLang, onSendToChat]);

  const formatNoteField = (fieldLabel, value) => {
    if (!value) return null;
    const blocks = parseLabeledBilingualBlocks(value);
    if (blocks) {
      return `${fieldLabel}:\n${blocks.map(({ label, content }) => `  [${label}]\n  ${content.replace(/\n/g, '\n  ')}`).join('\n')}`;
    }
    return `${fieldLabel}: ${value}`;
  };

  const speakerNotes = slides
    .map((s, i) => {
      if (s.type === 'pretext' && s.pretext) {
        const pt = s.pretext;
        const fields = [
          formatNoteField('Persona', pt.personaName),
          formatNoteField('Role', pt.personaRole),
          formatNoteField('Affiliation', pt.orgAffiliation),
          formatNoteField('Why credible', pt.credibilityReason),
          formatNoteField('Legend', pt.legend),
          formatNoteField('Channel', pt.channel),
          formatNoteField('Objective', pt.primaryObjective),
          formatNoteField('Success', pt.successDefinition),
          formatNoteField('Opening hook', pt.openingHook),
          formatNoteField('Immediate credibility', pt.immediateCredibility),
          formatNoteField('Conversation flow', pt.conversationFlow),
          formatNoteField('Objective delivery', pt.objectiveDelivery),
          formatNoteField('Full script', pt.fullScript),
          formatNoteField('Notes', pt.additionalNotes),
        ].filter(Boolean);
        return `--- Slide ${i + 1}: ${s.title} ---\n${fields.join('\n')}`;
      }
      return `--- Slide ${i + 1}: ${s.title} ---\n${(s.bullets || []).join('\n')}`;
    })
    .join('\n\n');

  const viewBtn = (id, label, disabled = false) => (
    <button
      type="button"
      onClick={() => { if (!disabled) setActiveView(id); }}
      disabled={disabled}
      className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
        activeView === id
          ? 'bg-teal-500/20 text-teal-300 border border-teal-500/40'
          : disabled
          ? 'bg-gray-800/40 text-gray-600 border border-transparent cursor-not-allowed'
          : 'bg-gray-800/80 text-gray-400 border border-transparent hover:text-gray-200 hover:bg-gray-800'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="flex flex-col h-full min-h-0 bg-black">

      {/* ── Panel header ── */}
      <div className="shrink-0 border-b border-teal-500/20 bg-gray-950 px-5 py-3 flex items-center gap-4">
        <div>
          <p className="text-sm font-semibold text-teal-400">Report Builder</p>
          <p className="text-[11px] text-gray-500 leading-tight">
            Paste raw notes → auto-structure → report + slides
          </p>
        </div>
        <div className="flex-1" />
        <div className="flex gap-1.5">
          {viewBtn('input', 'Input')}
          {viewBtn('report', hasOutput ? 'Report' : 'Report', !hasOutput)}
          {viewBtn('slides', hasOutput ? `Slides (${slides.length})` : 'Slides', !hasOutput)}
        </div>
      </div>

      {/* ── INPUT VIEW ── */}
      {activeView === 'input' && (
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-5 space-y-5">
          {/* Metadata grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-400">Client / Target</label>
              <input
                type="text"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                placeholder="e.g. Acme Corp"
                className="w-full px-3 py-2 bg-gray-900 border border-teal-500/30 rounded-lg text-white text-sm placeholder-gray-600 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-400">Prepared For</label>
              <input
                type="text"
                value={preparedFor}
                onChange={(e) => setPreparedFor(e.target.value)}
                placeholder="e.g. CISO, Security Team"
                className="w-full px-3 py-2 bg-gray-900 border border-teal-500/30 rounded-lg text-white text-sm placeholder-gray-600 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-400">Report Type</label>
              <select
                value={engagementType}
                onChange={(e) => setEngagementType(e.target.value)}
                className="w-full px-3 py-2 bg-gray-900 border border-teal-500/30 rounded-lg text-white text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              >
                {ENGAGEMENT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-400">Classification</label>
              <select
                value={classification}
                onChange={(e) => setClassification(e.target.value)}
                className="w-full px-3 py-2 bg-gray-900 border border-teal-500/30 rounded-lg text-white text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              >
                {CLASSIFICATION_OPTIONS.filter((o) => o.value !== '__custom__').map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-400">Report Title (optional)</label>
              <input
                type="text"
                value={reportTitle}
                onChange={(e) => setReportTitle(e.target.value)}
                placeholder="Auto-generated from type + client"
                className="w-full px-3 py-2 bg-gray-900 border border-teal-500/30 rounded-lg text-white text-sm placeholder-gray-600 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-400">Date (optional)</label>
              <input
                type="text"
                value={reportDate}
                onChange={(e) => setReportDate(e.target.value)}
                placeholder="e.g. June 2026"
                className="w-full px-3 py-2 bg-gray-900 border border-teal-500/30 rounded-lg text-white text-sm placeholder-gray-600 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <label className="text-xs font-medium text-gray-400">
                Secondary Language for Bilingual Output{' '}
                <span className="text-gray-600">(optional — e.g. Arabic, French, Spanish)</span>
              </label>
              <input
                type="text"
                value={secondaryLang}
                onChange={(e) => setSecondaryLang(e.target.value)}
                placeholder="Leave blank for single-language output"
                className="w-full px-3 py-2 bg-gray-900 border border-teal-500/30 rounded-lg text-white text-sm placeholder-gray-600 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>
          </div>

          {/* Raw source material */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-gray-400">Raw Source Material</label>
              <span className="text-[11px] text-gray-600">
                Paste notes, findings, bullets, transcripts — any format
              </span>
            </div>
            <textarea
              value={rawInput}
              onChange={(e) => setRawInput(e.target.value)}
              rows={14}
              placeholder={`Paste your source material here. Any format is accepted.\n\nTip: Use section headings to help organize the output:\n\n## Executive Summary\nThis assessment evaluated...\n\n## Key Findings\n- Employees disclosed credentials when contacted by a convincing IT persona\n- No caller ID verification in place for internal support desk requests\n- 4 of 6 targeted employees provided network access credentials\n\n## Risk Factors\n- High susceptibility to authority-based pretexts\n\n## Recommendations\n- Implement mandatory callback verification procedures\n- Deploy MFA across all privileged systems\n\n## Next Steps\n- Schedule debrief within 5 business days\n- Deliver final report by end of month`}
              className="w-full px-4 py-3 bg-gray-900 border border-teal-500/30 rounded-lg text-white text-sm placeholder-gray-600 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 resize-y font-mono leading-relaxed"
            />
          </div>

          {/* Action row */}
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={handleGenerate}
              disabled={!rawInput.trim()}
              className="px-5 py-2.5 bg-teal-500 hover:bg-teal-400 disabled:bg-gray-700 disabled:cursor-not-allowed text-black font-semibold rounded-lg text-sm transition-all duration-200"
            >
              Generate Report + Slides
            </button>
            {hasOutput && (
              <span className="text-xs text-gray-500">
                Last run: {reportData?.title} · {slides.length} slides
              </span>
            )}
          </div>

          {/* How-to callout */}
          {!rawInput.trim() && (
            <div className="rounded-lg border border-teal-500/20 bg-teal-950/20 p-4">
              <p className="text-sm font-semibold text-teal-400 mb-1">How to use Report Builder</p>
              <p className="text-sm text-gray-300 leading-relaxed">
                Paste any raw material — field notes, bullet points, vishing transcripts, OSINT
                findings — in any format. The builder auto-structures it into a professional
                consulting report and matching slide deck. Use markdown headings{' '}
                <code className="text-teal-300 bg-gray-800 px-1 rounded text-xs">## Section</code>{' '}
                or ALL CAPS labels to define sections explicitly. Then preview the Report and Slides
                tabs to copy your deliverables, or send the draft to the AI for LLM enhancement.
              </p>
            </div>
          )}
        </div>
      )}

      {/* ── REPORT VIEW ── */}
      {activeView === 'report' && reportData && (
        <div className="flex flex-col flex-1 min-h-0">
          {/* Report toolbar */}
          <div className="shrink-0 px-5 py-2.5 border-b border-teal-500/10 bg-gray-950 flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-white truncate">
              {reportTitle || reportData.title}{clientName ? ` — ${clientName}` : ''}
            </span>
            <span className={`px-2 py-0.5 rounded text-[10px] font-bold tracking-wider ${classificationClass(classification)}`}>
              {classification}
            </span>
            <div className="flex-1" />
            {onSendToChat && (
              <button
                type="button"
                onClick={handleSendToChat}
                className="px-3 py-1.5 bg-teal-500/10 hover:bg-teal-500/20 border border-teal-500/30 text-teal-300 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5"
                title="Send structured draft to AI for LLM enhancement"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                Send to AI
              </button>
            )}
            <button
              type="button"
              onClick={() => copyText(mdReport, 'md')}
              className="px-3 py-1.5 bg-gray-900 hover:bg-gray-800 border border-teal-500/30 text-white rounded-lg text-xs font-medium transition-colors"
            >
              {copyFeedback === 'md' ? '✓ Copied!' : 'Copy Markdown'}
            </button>
          </div>

          {/* Report body */}
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
            <div className="max-w-3xl mx-auto px-8 py-8 space-y-8">
              {/* Bilingual banner */}
              {reportData.secondaryLang && (
                <div className="rounded-lg border border-teal-500/30 bg-teal-950/20 px-4 py-2 flex items-center gap-2">
                  <span className="text-teal-400 text-xs font-semibold">Bilingual Output</span>
                  <span className="text-gray-400 text-xs">English + {reportData.secondaryLang}</span>
                  <span className="text-gray-600 text-xs ml-auto">Use "Send to AI" to generate full bilingual content</span>
                </div>
              )}
              {/* Meta block */}
              <div className="border-b border-teal-500/10 pb-6">
                <h1 className="text-2xl font-bold text-white mb-3">
                  {reportTitle || reportData.title}
                  {clientName ? <span className="text-teal-400"> — {clientName}</span> : null}
                </h1>
                <div className="text-xs text-gray-400 space-y-0.5">
                  {(preparedFor || clientName) && (
                    <p>Prepared for: <span className="text-gray-300">{preparedFor || clientName}</span></p>
                  )}
                  <p>Date: <span className="text-gray-300">{reportDate || reportData.date}</span></p>
                  <p>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold tracking-wider ${classificationClass(classification)}`}>
                      {classification}
                    </span>
                  </p>
                </div>
              </div>

              {/* 1. Executive Summary */}
              {reportData.executiveSummary.length > 0 && (
                <div className="space-y-3">
                  <SectionHeading>Executive Summary</SectionHeading>
                  {reportData.executiveSummary.map((p, i) => (
                    <p key={i} className="text-gray-200 text-sm leading-relaxed">{p}</p>
                  ))}
                </div>
              )}

              {/* Non-pretext sections */}
              {reportData.sections.map((s, i) => (
                <div key={i} className="space-y-3">
                  <SectionHeading>{s.heading}</SectionHeading>
                  <BulletList items={s.points} />
                </div>
              ))}

              {reportData.riskFactors.length > 0 && (
                <div className="space-y-3">
                  <SectionHeading>Risk Factors</SectionHeading>
                  <div className="rounded-lg border border-amber-500/20 bg-amber-950/10 p-4">
                    <BulletList items={reportData.riskFactors} />
                  </div>
                </div>
              )}

              {reportData.keyFindings.length > 0 && (
                <div className="space-y-3">
                  <SectionHeading>Key Findings</SectionHeading>
                  <BulletList items={reportData.keyFindings} numbered />
                </div>
              )}

              {/* 2. Developed Pretexts (simple list: name + channel) */}
              {reportData.pretextList?.length > 0 && (
                <div className="space-y-3">
                  <SectionHeading>Developed Pretexts</SectionHeading>
                  <ul className="space-y-1.5">
                    {reportData.pretextList.map((pt, i) => {
                      const label = pt.personaName || pt.primaryObjective || `Pretext ${i + 1}`;
                      return (
                        <li key={i} className="flex items-start gap-2 text-gray-200 text-sm">
                          <span className="text-teal-400 mt-0.5 shrink-0">{i + 1}.</span>
                          <span>
                            {label}
                            {pt.channel && (
                              <span className="text-gray-500 ml-2">— Channel: {pt.channel}</span>
                            )}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {/* 3. Detailed Pretexts (each expanded) */}
              {reportData.pretextList?.length > 0 && (
                <div className="space-y-4">
                  <SectionHeading>Detailed Pretexts</SectionHeading>
                  {reportData.pretextList.map((pt, i) => (
                    <PretextCard key={i} pretext={pt} index={i} />
                  ))}
                </div>
              )}

              {/* 4. Exercise Execution Risk Summary */}
              {reportData.exerciseRiskSummary && (
                <div className="space-y-3">
                  <SectionHeading>Operational Execution Risk Summary</SectionHeading>
                  <div className="rounded-lg border border-amber-500/20 bg-amber-950/10 p-4">
                    <p className="text-gray-200 text-sm leading-relaxed whitespace-pre-line">
                      {reportData.exerciseRiskSummary}
                    </p>
                  </div>
                </div>
              )}

              {/* 5. Recommendations */}
              {reportData.recommendations.length > 0 && (
                <div className="space-y-3">
                  <SectionHeading>Recommendations</SectionHeading>
                  <BulletList items={reportData.recommendations} numbered />
                </div>
              )}

              {reportData.nextSteps.length > 0 && (
                <div className="space-y-3">
                  <SectionHeading>Next Steps</SectionHeading>
                  <BulletList items={reportData.nextSteps} />
                </div>
              )}

              <div className="border-t border-teal-500/10 pt-4">
                <p className="text-xs text-gray-500 italic">
                  All information is confidential.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── SLIDES VIEW ── */}
      {activeView === 'slides' && slides.length > 0 && (
        <div className="flex flex-col flex-1 min-h-0">
          {/* Slides toolbar */}
          <div className="shrink-0 px-5 py-2.5 border-b border-teal-500/10 bg-gray-950 flex items-center gap-2">
            <span className="text-xs text-gray-400">
              Slide {activeSlide + 1} of {slides.length}
            </span>
            <div className="flex-1" />
            <button
              type="button"
              disabled={activeSlide === 0}
              onClick={() => setActiveSlide((p) => Math.max(0, p - 1))}
              className="px-2.5 py-1.5 bg-gray-900 hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed border border-teal-500/20 text-white rounded-lg text-xs transition-colors"
            >
              ← Prev
            </button>
            <button
              type="button"
              disabled={activeSlide >= slides.length - 1}
              onClick={() => setActiveSlide((p) => Math.min(slides.length - 1, p + 1))}
              className="px-2.5 py-1.5 bg-gray-900 hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed border border-teal-500/20 text-white rounded-lg text-xs transition-colors"
            >
              Next →
            </button>
            <button
              type="button"
              onClick={() => copyText(speakerNotes, 'notes')}
              className="px-3 py-1.5 bg-gray-900 hover:bg-gray-800 border border-teal-500/30 text-white rounded-lg text-xs font-medium transition-colors"
            >
              {copyFeedback === 'notes' ? '✓ Copied!' : 'Copy Speaker Notes'}
            </button>
            <button
              type="button"
              onClick={() => copyText(mdReport, 'md2')}
              className="px-3 py-1.5 bg-gray-900 hover:bg-gray-800 border border-teal-500/30 text-white rounded-lg text-xs font-medium transition-colors"
            >
              {copyFeedback === 'md2' ? '✓ Copied!' : 'Copy Markdown'}
            </button>
          </div>

          {/* Slide area + thumbnail strip */}
          <div className="flex flex-1 min-h-0">
            {/* Main slide */}
            <div className="flex-1 min-w-0 overflow-hidden p-5 flex flex-col justify-center">
              <SlidePreview
                slide={slides[activeSlide]}
                slideIndex={activeSlide}
                totalSlides={slides.length}
                clientName={clientName}
                classification={classification}
                reportDate={reportDate || reportData?.date}
              />
              {/* Speaker notes */}
              {slides[activeSlide]?.bullets?.length > 0 && (
                <div className="mt-3 rounded-lg bg-gray-900 border border-teal-500/10 px-4 py-3">
                  <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                    Speaker Notes
                  </p>
                  {slides[activeSlide].type === 'pretext' && slides[activeSlide].pretext ? (
                    <div className="space-y-2">
                      {[
                        ['Persona', slides[activeSlide].pretext.personaName],
                        ['Role', slides[activeSlide].pretext.personaRole],
                        ['Objective', slides[activeSlide].pretext.primaryObjective],
                        ['Opening hook', slides[activeSlide].pretext.openingHook],
                        ['Credibility', slides[activeSlide].pretext.immediateCredibility],
                      ].filter(([, v]) => v).map(([k, v]) => {
                        const blocks = parseLabeledBilingualBlocks(v);
                        return (
                          <div key={k} className={blocks ? 'space-y-1' : 'flex gap-2 text-xs'}>
                            <span className="text-gray-500 shrink-0 w-20 text-xs">{k}</span>
                            {blocks ? (
                              <div className="pl-1 space-y-1.5">
                                {blocks.map(({ label, content }, bi) => (
                                  <div key={bi}>
                                    <span className={`inline-block text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${
                                      bi === 0 ? 'border-teal-500/40 text-teal-400 bg-teal-950/30' : 'border-cyan-500/40 text-cyan-400 bg-cyan-950/30'
                                    }`}>{label}</span>
                                    {content && <span className="block text-xs text-gray-300">{content}</span>}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-gray-300 text-xs">{v}</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <ul className="space-y-1">
                      {slides[activeSlide].bullets.map((b, i) => (
                        <li key={i} className="text-xs text-gray-300">{b}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>

            {/* Thumbnail strip */}
            <div className="w-36 shrink-0 border-l border-teal-500/10 bg-gray-950 overflow-y-auto p-2 space-y-1.5">
              {slides.map((s, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setActiveSlide(i)}
                  className={`w-full text-left p-2 rounded-md border transition-colors ${
                    i === activeSlide
                      ? 'bg-teal-500/10 border-teal-500/40 text-teal-300'
                      : 'bg-gray-900 border-gray-800 text-gray-400 hover:border-teal-500/20 hover:text-gray-200'
                  }`}
                >
                  <span className="block text-[9px] text-gray-600 mb-0.5">{i + 1}</span>
                  <span className={`block text-[11px] font-medium leading-tight ${i === activeSlide ? 'text-teal-300' : 'text-gray-400'}`}>
                    {s.title}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
